<template>
  <div>
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <b-card-title class="mb-0">
            일자별 테마 분석
          </b-card-title>
          <small class="text-muted">분석일 이전 1년 동안 상위 1,2위였던 테마와 당시 주도주 조정 후보를 확인합니다.</small>
        </div>
        <div class="d-flex align-items-center flex-wrap">
          <b-button
            variant="outline-primary"
            class="mr-50 mb-50 mb-sm-0"
            :disabled="runLoading"
            @click="runAnalysis()"
          >
            <b-spinner
              v-if="runLoading"
              small
              class="mr-50"
            />
            최신 분석 실행
          </b-button>
          <b-button
            variant="primary"
            :disabled="loading"
            @click="fetchRecent"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-50"
            />
            새로고침
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
        >
          {{ errorMessage }}
        </b-alert>

        <b-alert
          v-if="runMessage"
          variant="success"
          show
        >
          {{ runMessage }}
        </b-alert>

        <div
          v-if="loading"
          class="text-center py-3"
        >
          <b-spinner />
        </div>

        <div
          v-else-if="recentDays.length === 0"
          class="text-center text-muted py-3"
        >
          저장된 일자별 테마 분석이 없습니다.
        </div>

        <div
          v-else
          class="analysis-day-grid"
        >
          <div
            v-for="day in recentDays"
            :key="day.baseYmd"
            class="analysis-day-card"
          >
            <div class="analysis-day-head">
              <div>
                <div class="analysis-day-date">
                  {{ formatDateLabel(day.baseYmd) }} ({{ day.dayOfWeek }})
                </div>
                <div class="analysis-day-summary">
                  {{ day.summary || '-' }}
                </div>
              </div>
              <div class="analysis-day-actions">
                <b-badge :variant="day.aiUsed ? 'light-success' : 'light-secondary'">
                  {{ day.aiUsed ? 'AI 적용' : 'AI 미적용' }}
                </b-badge>
                <b-button
                  size="sm"
                  variant="flat-primary"
                  class="mt-50"
                  :disabled="runLoading"
                  @click="runAnalysis(day.baseYmd)"
                >
                  재분석
                </b-button>
              </div>
            </div>

            <div class="analysis-meta-row">
              <span>테마 {{ day.analyzedThemeCount || 0 }}개</span>
              <span>후보 {{ day.candidateCount || 0 }}건</span>
              <span :class="day.status === 'COMPLETED' ? 'text-success' : 'text-warning'">{{ day.status }}</span>
            </div>

            <div class="analysis-ai-stock-panel">
              <div class="analysis-section-label">
                AI 유효 종목
              </div>
              <div
                v-if="topAiCandidates(day).length === 0"
                class="analysis-empty-text"
              >
                AI 유효 후보가 없습니다.
              </div>
              <button
                v-for="item in topAiCandidates(day)"
                :key="`${day.baseYmd}-candidate-${item.stockCode}`"
                type="button"
                class="analysis-ai-stock-entry"
                @click="openChartModal(item)"
              >
                <div class="analysis-ai-stock-top">
                  <strong>{{ item.stockName }}</strong>
                  <span class="analysis-ai-stock-score">{{ formatDecimal(item.candidateScore, 1) }}점</span>
                </div>
                <div class="analysis-ai-stock-bottom">
                  <span :class="rateClass(item.adjustmentRate)">{{ formatRate(item.adjustmentRate) }}</span>
                  <span class="analysis-ai-stock-caption">고점대비 조정</span>
                </div>
              </button>
            </div>

            <div
              v-if="isExpanded(day.baseYmd)"
              class="analysis-theme-list"
            >
              <div class="analysis-section-label">
                테마별 리스트
              </div>
              <button
                v-for="theme in visibleThemes(day)"
                :key="`${day.baseYmd}-${theme.themeNo}`"
                type="button"
                class="analysis-theme-entry"
                @click="openDayDetail(day.baseYmd, theme.themeNo)"
              >
                <div class="analysis-theme-line">
                  <strong>{{ theme.themeName }}</strong>
                  <span :class="['analysis-rate', rateClass(theme.changeRate)]">{{ formatRate(theme.changeRate) }}</span>
                </div>
                <div class="analysis-theme-subline">
                  <span :class="theme.aiAlive === false ? 'text-warning' : 'text-success'">
                    {{ aliveLabel(theme) }}
                  </span>
                  <span>후보 {{ theme.candidateCount || 0 }}건</span>
                </div>
              </button>
            </div>

            <button
              v-else
              type="button"
              class="analysis-more-inline"
              @click="toggleDay(day.baseYmd)"
            >
              테마별 리스트 더보기
            </button>

            <button
              v-if="isExpanded(day.baseYmd)"
              type="button"
              class="analysis-more-inline"
              @click="toggleDay(day.baseYmd)"
            >
              접기
            </button>

            <div
              v-if="day.aiSummary"
              class="analysis-ai-preview"
            >
              {{ firstLine(day.aiSummary) }}
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-modal
      v-model="detailModalVisible"
      size="xl"
      hide-footer
      :title="detailTitle"
    >
      <b-alert
        v-if="detailErrorMessage"
        variant="danger"
        show
      >
        {{ detailErrorMessage }}
      </b-alert>

      <div
        v-if="detailLoading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>

      <div v-else>
        <div class="detail-summary-box mb-1">
          <div class="mb-25">
            <strong>요약</strong>
          </div>
          <div class="text-muted">
            {{ selectedDay.summary || '-' }}
          </div>
          <div
            v-if="selectedDay.aiSummary"
            class="detail-ai-summary mt-75"
          >
            {{ selectedDay.aiSummary }}
          </div>
        </div>

        <div class="theme-chip-row mb-1">
          <button
            v-for="theme in detailThemes"
            :key="`detail-${theme.themeNo}`"
            type="button"
            :class="['theme-chip', { active: selectedThemeNo === theme.themeNo }]"
            @click="selectedThemeNo = theme.themeNo"
          >
            {{ theme.themeName }} / {{ theme.candidateCount || 0 }}건
          </button>
        </div>

        <div
          v-if="selectedTheme"
          class="selected-theme-panel"
        >
          <div class="selected-theme-head">
            <div>
              <h5 class="mb-25">
                {{ selectedTheme.themeName }}
              </h5>
              <div class="selected-theme-metrics">
                <span>최근등장 {{ formatRate(selectedTheme.changeRate) }}</span>
                <span>최근3일 {{ formatRate(selectedTheme.recent3daysChangeRate) }}</span>
                <span>후보 {{ selectedTheme.candidateCount || 0 }}건</span>
              </div>
            </div>
            <div class="selected-theme-status">
              <b-badge :variant="aliveBadgeVariant(selectedTheme)">
                {{ aliveLabel(selectedTheme) }}
              </b-badge>
            </div>
          </div>

          <div class="selected-theme-reason-grid">
            <div class="reason-card">
              <small class="text-muted d-block mb-25">정량 판단</small>
              <div>{{ selectedTheme.heuristicAliveReason || '-' }}</div>
            </div>
            <div class="reason-card">
              <small class="text-muted d-block mb-25">AI 판단</small>
              <div>{{ selectedTheme.aiReason || '-' }}</div>
            </div>
            <div class="reason-card">
              <small class="text-muted d-block mb-25">체크 포인트</small>
              <div>{{ selectedTheme.aiFocusPoint || '-' }}</div>
            </div>
          </div>

          <b-table
            class="mt-1"
            :items="selectedTheme.candidates || []"
            :fields="candidateFields"
            responsive
            small
            show-empty
            empty-text="조건에 맞는 조정 후보가 없습니다."
            @row-clicked="openChartModal"
          >
            <template #cell(currentPrice)="row">
              {{ formatNumber(row.item.currentPrice) }}
            </template>
            <template #cell(adjustmentRate)="row">
              <span :class="row.item.qualifiesAdjustment ? 'text-success' : 'text-muted'">{{ formatRate(row.item.adjustmentRate) }}</span>
            </template>
            <template #cell(avgTradeAmount20)="row">
              {{ formatDecimal(row.item.avgTradeAmount20, 1) }}억
            </template>
            <template #cell(candidateScore)="row">
              {{ formatDecimal(row.item.candidateScore, 1) }}
            </template>
            <template #cell(aiPriority)="row">
              <b-badge
                v-if="row.item.aiPriority"
                :variant="priorityVariant(row.item.aiPriority)"
              >
                {{ row.item.aiPriority }}
              </b-badge>
              <span v-else>-</span>
            </template>
          </b-table>

          <div
            v-if="selectedTheme.candidates && selectedTheme.candidates.length > 0"
            class="candidate-opinion-list"
          >
            <div
              v-for="candidate in selectedTheme.candidates"
              :key="`opinion-${candidate.stockCode}`"
              class="candidate-opinion-item"
            >
              <strong>{{ candidate.stockName }} ({{ candidate.stockCode }})</strong>
              <span class="text-muted ml-50">{{ candidate.aiOpinion || 'AI 의견 없음' }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="theme-analysis-stock-chart-modal"
      v-model="chartModalVisible"
      size="xl"
      :title="`${currentChartLabel} 차트`"
      hide-footer
    >
      <div class="mb-1 font-weight-bold">
        {{ selectedItem.name }} ({{ selectedItem.code }})
      </div>

      <div class="mb-1 d-flex align-items-center">
        <span class="mr-50">차트구분</span>
        <b-form-select
          v-model="chartType"
          :options="chartTypeOptions"
          size="sm"
          style="width: 140px;"
        />
        <template v-if="chartType === 'M'">
          <span class="ml-1 mr-50">분봉</span>
          <b-form-select
            v-model="minuteScope"
            :options="minuteScopeOptions"
            size="sm"
            style="width: 140px;"
          />
        </template>
      </div>

      <b-alert
        v-if="chartErrorMessage"
        variant="danger"
        show
      >
        {{ chartErrorMessage }}
      </b-alert>

      <div
        v-if="chartLoading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>

      <vue-apex-charts
        v-else
        type="candlestick"
        height="420"
        :options="chartOptions"
        :series="chartSeries"
      />
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import VueApexCharts from 'vue-apexcharts'
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormSelect,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormSelect,
    BModal,
    BSpinner,
    BTable,
    VueApexCharts,
  },
  data() {
    return {
      loading: false,
      runLoading: false,
      errorMessage: '',
      runMessage: '',
      recentDays: [],
      expandedDays: {},
      detailCache: {},
      detailModalVisible: false,
      detailLoading: false,
      detailErrorMessage: '',
      selectedDay: {},
      selectedThemeNo: null,
      chartErrorMessage: '',
      chartLoading: false,
      chartModalVisible: false,
      selectedItem: {
        code: '',
        name: '',
      },
      chartSeries: [{ data: [] }],
      chartType: 'D',
      chartTypeOptions: [
        { value: 'M', text: '분봉' },
        { value: 'D', text: '일봉' },
        { value: 'W', text: '주봉' },
        { value: 'MO', text: '월봉' },
      ],
      minuteScope: '3',
      minuteScopeOptions: [
        { value: '1', text: '1분봉' },
        { value: '3', text: '3분봉' },
        { value: '5', text: '5분봉' },
        { value: '10', text: '10분봉' },
      ],
      candidateFields: [
        { key: 'itemRankNo', label: '순번' },
        { key: 'stockCode', label: '종목코드' },
        { key: 'stockName', label: '종목명' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'adjustmentRate', label: '고점대비조정' },
        { key: 'avgTradeAmount20', label: '20일평균대금' },
        { key: 'candidateScore', label: '점수' },
        { key: 'aiPriority', label: 'AI우선도' },
      ],
      chartOptions: {
        chart: {
          type: 'candlestick',
          toolbar: { show: true },
        },
        plotOptions: {
          candlestick: {
            colors: {
              upward: '#ff3b30',
              downward: '#2f80ed',
            },
          },
        },
        xaxis: {
          type: 'category',
        },
        tooltip: {
          x: {
            formatter: value => value || '',
          },
        },
        yaxis: {
          tooltip: { enabled: true },
          labels: {
            formatter: value => Number(value).toLocaleString(),
          },
        },
        noData: {
          text: '차트 데이터가 없습니다.',
        },
      },
    }
  },
  computed: {
    detailThemes() {
      return Array.isArray(this.selectedDay.themes) ? this.selectedDay.themes : []
    },
    selectedTheme() {
      return this.detailThemes.find(theme => theme.themeNo === this.selectedThemeNo) || null
    },
    detailTitle() {
      if (!this.selectedDay.baseYmd) return '일자별 테마 분석 상세'
      return `${this.selectedDay.baseYmd} 일자별 테마 분석`
    },
    currentChartLabel() {
      const found = this.chartTypeOptions.find(option => option.value === this.chartType)
      return found ? found.text : '일봉'
    },
  },
  watch: {
    chartType() {
      if (this.chartModalVisible && this.selectedItem.code) {
        this.fetchChart()
      }
    },
    minuteScope() {
      if (this.chartModalVisible && this.selectedItem.code && this.chartType === 'M') {
        this.fetchChart()
      }
    },
  },
  created() {
    this.fetchRecent()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    async fetchRecent() {
      this.loading = true
      this.errorMessage = ''
      this.runMessage = ''
      try {
        const response = await axios.get('/api/kiwoom/theme-analysis/recent')
        const data = this.parseApiResponse(response)
        this.recentDays = Array.isArray(data.days) ? data.days : []
        this.expandedDays = {}
      } catch (error) {
        this.recentDays = []
        this.errorMessage = error && error.message ? error.message : '일자별 테마 분석 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async runAnalysis(baseYmd) {
      this.runLoading = true
      this.errorMessage = ''
      this.runMessage = ''
      try {
        const response = await axios.post('/api/kiwoom/theme-analysis/run', baseYmd ? { baseYmd } : {})
        const data = this.parseApiResponse(response)
        this.runMessage = `${data.baseYmd} 분석을 완료했습니다.`
        this.detailCache[data.baseYmd] = data
        await this.fetchRecent()
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : '테마 분석 실행에 실패했습니다.'
      } finally {
        this.runLoading = false
      }
    },
    async openDayDetail(baseYmd, themeNo) {
      this.detailModalVisible = true
      this.detailLoading = true
      this.detailErrorMessage = ''
      this.selectedDay = {}
      this.selectedThemeNo = themeNo || null
      try {
        let dayData = this.detailCache[baseYmd]
        if (!dayData) {
          const response = await axios.post('/api/kiwoom/theme-analysis/date', { baseYmd })
          dayData = this.parseApiResponse(response)
          this.$set(this.detailCache, baseYmd, dayData)
        }
        this.selectedDay = dayData || {}
        if (!this.selectedThemeNo && Array.isArray(dayData.themes) && dayData.themes.length > 0) {
          this.selectedThemeNo = dayData.themes[0].themeNo
        }
      } catch (error) {
        this.detailErrorMessage = error && error.message ? error.message : '상세 분석 조회에 실패했습니다.'
      } finally {
        this.detailLoading = false
      }
    },
    visibleThemes(day) {
      const themes = Array.isArray(day && day.themes) ? day.themes : []
      return themes
    },
    isExpanded(baseYmd) {
      return this.expandedDays[baseYmd] === true
    },
    toggleDay(baseYmd) {
      this.$set(this.expandedDays, baseYmd, !this.isExpanded(baseYmd))
    },
    async openChartModal(item) {
      if (!item || !item.stockCode) return
      this.selectedItem = {
        code: item.stockCode,
        name: item.stockName || '',
      }
      this.chartType = 'D'
      this.minuteScope = '3'
      this.chartModalVisible = true
      await this.fetchChart()
    },
    async fetchChart() {
      if (!this.selectedItem.code) return
      this.chartErrorMessage = ''
      this.chartSeries = [{ data: [] }]
      this.chartLoading = true
      try {
        const response = await axios.post('/api/kiwoom/stock-item/chart/daily', {
          code: this.selectedItem.code,
          chartType: this.chartType,
          ticScope: this.chartType === 'M' ? this.minuteScope : undefined,
          count: 120,
        })
        const data = this.parseApiResponse(response)
        const candles = Array.isArray(data.candles) ? data.candles : []
        const seriesData = candles
          .map(candle => {
            const x = this.formatCategoryDate(candle.dt)
            if (!x) return null
            return {
              x,
              y: [
                this.toNumber(candle.openPrice),
                this.toNumber(candle.highPrice),
                this.toNumber(candle.lowPrice),
                this.toNumber(candle.closePrice),
              ],
            }
          })
          .filter(Boolean)
        this.chartSeries = [{ data: seriesData }]
      } catch (error) {
        this.chartErrorMessage = error && error.message ? error.message : '차트 조회에 실패했습니다.'
      } finally {
        this.chartLoading = false
      }
    },
    aliveLabel(theme) {
      if (theme.aiAlive === true) return 'AI 유효'
      if (theme.aiAlive === false) return 'AI 주의'
      return theme.heuristicAlive ? '정량 유효' : '정량 약화'
    },
    aliveBadgeVariant(theme) {
      if (theme.aiAlive === true) return 'light-success'
      if (theme.aiAlive === false) return 'light-warning'
      return theme.heuristicAlive ? 'light-primary' : 'light-secondary'
    },
    priorityVariant(priority) {
      if (priority === 'A') return 'success'
      if (priority === 'B') return 'primary'
      if (priority === 'C') return 'warning'
      return 'secondary'
    },
    firstLine(value) {
      return String(value || '').split('\n')[0]
    },
    topAiCandidates(day) {
      const themes = Array.isArray(day && day.themes) ? day.themes : []
      const candidateMap = new Map()
      themes
        .filter(theme => theme && theme.aiAlive === true)
        .forEach(theme => {
          const candidates = Array.isArray(theme.candidates) ? theme.candidates : []
          candidates.forEach(candidate => {
            if (!candidate || !candidate.stockCode) return
            const current = candidateMap.get(candidate.stockCode)
            if (!current || this.toNumber(candidate.candidateScore) > this.toNumber(current.candidateScore)) {
              candidateMap.set(candidate.stockCode, candidate)
            }
          })
        })
      return Array.from(candidateMap.values())
        .sort((a, b) => this.toNumber(b.candidateScore) - this.toNumber(a.candidateScore))
        .slice(0, 5)
    },
    formatDateLabel(baseYmd) {
      if (!baseYmd || baseYmd.length !== 8) return baseYmd
      return `${baseYmd.slice(0, 4)}.${baseYmd.slice(4, 6)}.${baseYmd.slice(6, 8)}`
    },
    toNumber(value) {
      if (value === null || value === undefined) return 0
      const cleaned = String(value).replace(/,/g, '')
      const parsed = Number(cleaned)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    formatCategoryDate(value) {
      const text = String(value || '').trim()
      if (text.length === 14) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}`
      }
      if (text.length >= 8) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
      }
      return text
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(String(value).replace(/,/g, ''))
      if (Number.isNaN(parsed)) return value
      return parsed.toLocaleString()
    },
    formatRate(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return value
      const prefix = parsed > 0 ? '+' : ''
      return `${prefix}${parsed.toFixed(2)}%`
    },
    formatDecimal(value, digits = 2) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return value
      return parsed.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    rateClass(value) {
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return 'text-body'
      if (parsed > 0) return 'text-danger'
      if (parsed < 0) return 'text-primary'
      return 'text-body'
    },
  },
}
</script>

<style scoped>
.analysis-day-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}

.analysis-day-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.9rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 6px 18px rgba(34, 41, 47, 0.06);
  padding: 1rem;
}

.analysis-day-head {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.analysis-day-date {
  font-size: 1rem;
  font-weight: 700;
  color: #2c2c3a;
}

.analysis-day-summary {
  margin-top: 0.35rem;
  font-size: 0.83rem;
  line-height: 1.45;
  color: #6e6b7b;
}

.analysis-day-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.analysis-meta-row {
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: #5e5873;
}

.analysis-ai-stock-panel,
.analysis-theme-list {
  margin-top: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.analysis-section-label {
  font-size: 0.78rem;
  font-weight: 800;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.analysis-empty-text {
  color: #b9b9c3;
  font-size: 0.82rem;
}

.analysis-ai-stock-entry {
  width: 100%;
  border: 1px solid #ebe9f1;
  border-radius: 0.7rem;
  background: linear-gradient(135deg, #fffdf7 0%, #fff 100%);
  padding: 0.65rem 0.75rem;
  text-align: left;
}

.analysis-ai-stock-entry:hover {
  border-color: #f59e0b;
  background: #fffaf0;
}

.analysis-ai-stock-top,
.analysis-ai-stock-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.analysis-ai-stock-score {
  color: #8c6a11;
  font-size: 0.82rem;
  font-weight: 800;
}

.analysis-ai-stock-bottom {
  margin-top: 0.2rem;
}

.analysis-ai-stock-caption {
  color: #8a8d93;
  font-size: 0.76rem;
}

.analysis-theme-entry {
  width: 100%;
  border: 1px solid #e5e3eb;
  border-radius: 0.8rem;
  background: #fff;
  padding: 0.75rem;
  text-align: left;
}

.analysis-theme-entry:hover {
  border-color: #0d6efd;
  background: #f7fbff;
}

.analysis-more-inline {
  margin-top: 0.7rem;
  border: 1px dashed #d8d6de;
  background: #f8f8f8;
  color: #5e5873;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.8rem;
}

.analysis-theme-line {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.analysis-theme-subline {
  margin-top: 0.35rem;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6e6b7b;
}

.analysis-rate {
  font-weight: 700;
}

.analysis-ai-preview {
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #d8d6de;
  font-size: 0.8rem;
  color: #5e5873;
}

.detail-summary-box {
  border: 1px solid #ebe9f1;
  border-radius: 0.8rem;
  background: #f9fafc;
  padding: 0.9rem;
}

.detail-ai-summary {
  white-space: pre-line;
  font-size: 0.86rem;
}

.theme-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.theme-chip {
  border: 1px solid #dcd9e6;
  background: #f4f4f8;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.82rem;
  color: #5e5873;
}

.theme-chip.active {
  border-color: #0d6efd;
  background: #eef5ff;
  color: #0d6efd;
}

.selected-theme-panel {
  border: 1px solid #ebe9f1;
  border-radius: 0.9rem;
  background: #fff;
  padding: 1rem;
}

.selected-theme-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.selected-theme-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.82rem;
  color: #6e6b7b;
}

.selected-theme-reason-grid {
  margin-top: 0.9rem;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.reason-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  background: #faf9fc;
  padding: 0.8rem;
  font-size: 0.84rem;
  line-height: 1.45;
}

.candidate-opinion-list {
  margin-top: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.candidate-opinion-item {
  font-size: 0.83rem;
}

@media (max-width: 991px) {
  .selected-theme-reason-grid {
    grid-template-columns: 1fr;
  }
}
</style>
